import { normalizeInsightSections, parseInsightSections, slugify, toCsvList } from "../components/insights/insight-utils";

const getApiBase = () => {
  const base = process.env.REACT_APP_AVANI_API_BASE_URL || "/api";
  return String(base).replace(/\/$/, "");
};

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const buildContentFromSections = (sections = []) =>
  normalizeInsightSections(sections)
    .map((section) => {
      const heading = section.heading ? `<h2>${escapeHtml(section.heading)}</h2>` : "";
      const paragraphs = (section.paragraphs || []).map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`).join("");
      const bullets = (section.bullets || []).length
        ? `<ul>${section.bullets.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul>`
        : "";
      return `<section>${heading}${paragraphs}${bullets}</section>`;
    })
    .join("\n");

const readDraftSections = (draft = {}) => {
  const sections = parseInsightSections(draft.bodySectionsText || draft.bodySections || []);
  return normalizeInsightSections(sections);
};

export const buildBackendArticlePayload = (draft = {}, existing = null) => {
  const sections = readDraftSections(draft);
  const slug = slugify(draft.slug || draft.title || existing?.slug || "");
  const content = typeof draft.content === "string" && draft.content.trim() ? draft.content.trim() : buildContentFromSections(sections);

  return {
    title: String(draft.title || existing?.title || "").trim(),
    slug,
    excerpt: String(draft.excerpt || existing?.excerpt || "").trim(),
    content,
    author: String(draft.authorName || draft.author || existing?.author || "Avani Cimcon Editorial Team").trim(),
    category: String(draft.category || existing?.category || "").trim(),
    tags: toCsvList(draft.tags || existing?.tags || []),
    cover_image_url: String(draft.coverImageUrl || draft.heroImage || existing?.cover_image_url || "").trim(),
    og_image_url: String(draft.ogImageUrl || draft.ogImage || existing?.og_image_url || draft.coverImageUrl || draft.heroImage || "").trim(),
    seo_title: String(draft.metaTitle || draft.seoTitle || existing?.seo_title || draft.title || "").trim(),
    seo_description: String(draft.metaDescription || draft.seoDescription || existing?.seo_description || draft.excerpt || "").trim(),
    seo_keywords: toCsvList(draft.keywords || draft.seoKeywords || existing?.seo_keywords || []),
    read_time: String(draft.readingTime || draft.readTime || existing?.read_time || "").trim(),
    published: true,
  };
};

const fetchJson = async (url, init = {}) => {
  const response = await fetch(url, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init.headers || {}),
    },
  });

  const raw = await response.text();
  let body = null;
  try {
    body = raw ? JSON.parse(raw) : null;
  } catch (err) {
    body = raw;
  }

  if (!response.ok) {
    const error = new Error(`Request failed: ${response.status} ${response.statusText}`);
    error.status = response.status;
    error.body = body;
    throw error;
  }

  return body;
};

const login = async (apiBase, email, password) => {
  const response = await fetchJson(`${apiBase}/admin/login`, {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });

  return response.token || response.accessToken || response.jwt || null;
};

const upsertArticle = async ({ apiBase, token, payload, existingId }) => {
  if (existingId) {
    return fetchJson(`${apiBase}/blog/admin/${existingId}`, {
      method: "PUT",
      headers: {
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
  }

  return fetchJson(`${apiBase}/blog/admin`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
};

export const publishDraftLive = async ({ draft, existing, email, password }) => {
  if (!email || !password) {
    throw new Error("Admin email and password are required to publish live.");
  }

  const apiBase = getApiBase();
  const token = await login(apiBase, email, password);
  if (!token) {
    throw new Error("Login succeeded but the backend did not return a token.");
  }

  const payload = buildBackendArticlePayload(draft, existing);
  if (!payload.title || !payload.slug) {
    throw new Error("A live publish requires both a title and a slug.");
  }

  const existingId =
    existing?.backendId ||
    existing?.remoteId ||
    existing?.apiId ||
    existing?.liveId ||
    existing?.publishedId ||
    existing?.idFromApi ||
    null;

  const saved = await upsertArticle({
    apiBase,
    token,
    payload,
    existingId,
  });

  const resolvedId =
    saved?.id ||
    saved?._id ||
    saved?.post?.id ||
    saved?.post?._id ||
    existingId ||
    null;
  const resolvedSlug = saved?.slug || saved?.post?.slug || payload.slug;
  const siteBase = String(process.env.REACT_APP_AVANI_SITE_BASE_URL || "https://www.avanicimcon.com").replace(/\/$/, "");

  return {
    apiBase,
    payload,
    saved,
    slug: resolvedSlug,
    id: resolvedId,
    publicUrl: `${siteBase}/blog/${resolvedSlug}`,
  };
};
